import { formatSummaryFirstLine } from '../../utils/summaryFormatter';

// Default timezone for summary dates (Thailand)
const SUMMARY_TIMEZONE = 'Asia/Bangkok';
const SUMMARY_TIMEZONE_LABEL = 'GMT+7';

/**
 * Get display name for summary owner. Prefer English / username to avoid incorrect Thai translation.
 * Use username (English) first; if no Thai provided, show in English (firstName_eng/lastName_eng or username).
 */
const getSummaryOwnerDisplayName = (user) => {
  if (!user) return 'Unknown';
  // Prefer username (typically English, not translated)
  if (user.username && String(user.username).trim()) return user.username.trim();
  // Prefer English name fields when available (firstName_eng/lastName_eng or firstNameEng/lastNameEng)
  const engFirst = user.firstName_eng ?? user.firstNameEng ?? '';
  const engLast = user.lastName_eng ?? user.lastNameEng ?? '';
  if (engFirst || engLast) {
    const engName = [engFirst, engLast].filter(Boolean).join(' ').trim();
    if (engName) return engName;
  }
  // Fallback to firstName + lastName (may be Thai)
  const first = user.firstName ?? user.FirstName ?? '';
  const last = user.lastName ?? user.LastName ?? '';
  if (first || last) {
    const name = [first, last].filter(Boolean).join(' ').trim();
    if (name) return name;
  }
  if (user.name && String(user.name).trim()) return user.name.trim();
  if (user.email && String(user.email).trim()) return user.email.trim();
  return 'Unknown';
};

/**
 * Format a date for summary with explicit timezone (Asia/Bangkok, GMT+7).
 */
const formatNoteDateWithTimezone = (dateValue) => {
  if (!dateValue) return 'No date';
  const date = new Date(dateValue);
  const formatted = date.toLocaleString('en-US', {
    timeZone: SUMMARY_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  return `${formatted} (${SUMMARY_TIMEZONE_LABEL})`;
};

// Helper function to resolve user ID from user object
const resolveUserId = (user) => {
  console.log('🆔 === RESOLVING USER ID ===');
  console.log('🆔 Full user object:', JSON.stringify(user, null, 2));
  
  if (!user) {
    console.warn('⚠️ USERID_DEBUG: User object is null/undefined');
    return null;
  }

  // CRITICAL: The backend uses req.user.tenantUserId for all tenant-specific operations
  // So we MUST prioritize tenantUserId over userId to ensure consistency
  // userId might be a global user ID in some contexts, so it should be a fallback only
  
  const candidates = [
    { name: 'tenantUserId', value: user.tenantUserId }, // PRIMARY: Explicit tenant user ID (used by backend)
    { name: 'UserID', value: user.UserID },           // FALLBACK 1: Database field name
    { name: 'userId', value: user.userId },           // FALLBACK 2: Could be global or tenant depending on context
    { name: 'userID', value: user.userID },           // FALLBACK 3: Alternative casing
    { name: 'UserId', value: user.UserId }            // FALLBACK 4: Alternative casing
  ];

  console.log('🆔 Checking candidates:', candidates.map(c => `${c.name}=${c.value}`).join(', '));

  for (const candidate of candidates) {
    if (candidate.value !== undefined && candidate.value !== null) {
      const trimmed = String(candidate.value).trim();
      if (trimmed !== '') {
        console.log('✅ USERID_DEBUG: Resolved user ID:', trimmed, 'from field:', candidate.name);
        return trimmed;
      }
    }
  }

  console.warn('⚠️ USERID_DEBUG: Failed to resolve user ID from user object');
  console.warn('⚠️ Available keys in user object:', Object.keys(user));
  return null;
};

// Gather notes for summary based on options
export const gatherNotesForSummary = async (options, user, getProtectedTenantCode) => {
  try {
    const tenantCode = getProtectedTenantCode();
    const selectedRole = localStorage.getItem('selectedRole');
    const headers = {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
      ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {}),
      ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
    };
    
    console.log('🔍 SUMMARY_FETCH: Request headers:', {
      hasToken: !!headers['Authorization'],
      tenantCode: tenantCode || 'none',
      selectedRole: selectedRole || 'none',
      scope: options.scope
    });

    // Start with base endpoint
    let endpoint = '/api/sales-notes';
    const params = new URLSearchParams();

    // Handle scope (my vs all)
    if (options.scope === 'my') {
      const userId = resolveUserId(user);
      if (userId) {
        params.append('userId', userId);
      }
    }

    // Handle filtering - note that multiple filters can be combined
    // Tag filtering
    if (options.filterBy === 'tag' && options.filterValue) {
      params.append('tag', options.filterValue);
    }
    
    // Date filtering (can be combined with other filters)
    if (options.filterBy === 'date' || (options.dateFrom || options.dateTo)) {
      if (options.dateFrom) {
        params.append('dateFrom', options.dateFrom);
      }
      if (options.dateTo) {
        params.append('dateTo', options.dateTo);
      }
    }
    
    // Entity filtering (can be combined with other filters)
    if (options.filterBy === 'entity' && options.entityType && options.entityId) {
      params.append('entityType', options.entityType);
      params.append('entityId', options.entityId);
    }
    
    // Note type filtering (can be combined with tag/date/entity filters)
    if (options.noteTypes && Array.isArray(options.noteTypes) && options.noteTypes.length > 0) {
      // Append each note type as a separate parameter (backend will handle comma-separated or array)
      options.noteTypes.forEach(type => {
        params.append('noteTypes', type);
      });
    }

    // Add limit to get all notes (up to a reasonable limit)
    params.append('limit', '500');

    const queryString = params.toString();
    const fullEndpoint = queryString ? `${endpoint}?${queryString}` : endpoint;

    console.log('📝 Gathering notes for summary:', fullEndpoint);

    const response = await fetch(fullEndpoint, { headers });

    if (response.ok) {
      const data = await response.json();
      const notes = data.notes || data || [];
      console.log(`📝 Gathered ${notes.length} notes for summary`);
      return notes;
    } else {
      throw new Error('Failed to fetch notes for summary');
    }
  } catch (error) {
    console.error('Error gathering notes:', error);
    throw error;
  }
};

// Chunk notes into smaller arrays
export const chunkNotes = (notes, maxChunkSize = 20) => {
  const chunks = [];
  for (let i = 0; i < notes.length; i += maxChunkSize) {
    chunks.push(notes.slice(i, i + maxChunkSize));
  }
  return chunks;
};

// Call AI API to generate summary
export const callAIForSummary = async (prompt, notes, getProtectedTenantCode, attachmentSummaries = []) => {
  try {
    const tenantCode = getProtectedTenantCode();
    
    // Format notes for AI (dates with timezone; author from FirstName/LastName, do not translate names)
    const notesText = notes.map((note, idx) => {
      const date = formatNoteDateWithTimezone(note.NoteDate);
      // Author: API returns FirstName, LastName (not AuthorName). Use as-is; prefer English if note had firstName_eng/lastName_eng.
      const authorEng = [note.FirstName_Eng || note.firstName_eng, note.LastName_Eng || note.lastName_eng].filter(Boolean).join(' ').trim();
      const author = note.AuthorName || authorEng || [note.FirstName, note.LastName].filter(Boolean).join(' ').trim() || 'Unknown';
      const tags = note.Tags || '';
      return `[Note ${idx + 1}] Date: ${date} | Author: ${author} | Tags: ${tags}\n${note.NoteText}\n`;
    }).join('\n---\n\n');

    // Format attachment summaries if provided
    let attachmentContext = '';
    if (attachmentSummaries && attachmentSummaries.length > 0) {
      attachmentContext = '\n\n## Attached Documents\n\n' + 
        attachmentSummaries.map((att, idx) => {
          return `[Attachment ${idx + 1}] ${att.fileName} (${att.fileType})\nSummary: ${att.summary}\n`;
        }).join('\n---\n\n');
    }

    const fullContent = notesText + attachmentContext;

    const requestBody = {
      message: prompt.replace('{notes}', fullContent),
      history: [],
      options: {
        agentType: 'note_summary',
        useSystemPrompt: false,
        temperature: 0.5,
        max_tokens: 3000
      }
    };

    console.log('🤖 Calling AI for summary...');

    const selectedRole = localStorage.getItem('selectedRole');
    const response = await fetch('/api/ai/chat/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {}),
        ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
      },
      body: JSON.stringify(requestBody)
    });

    if (response.ok) {
      const result = await response.json();
      console.log('🤖 AI Response:', result);
      
      // Backend returns: { message: "...", data: { reply: "...", ... } }
      const summaryContent = result.data?.reply || result.reply || result.response || '';
      
      if (!summaryContent) {
        console.error('❌ No summary content in response:', result);
        throw new Error('AI returned empty summary');
      }
      
      console.log('✅ Summary content length:', summaryContent.length);
      return summaryContent;
    } else {
      const errorData = await response.json();
      console.error('❌ AI API error:', errorData);
      throw new Error(errorData.message || 'AI summary generation failed');
    }
  } catch (error) {
    console.error('Error calling AI:', error);
    throw error;
  }
};

// Main function to generate summary
export const handleGenerateSummary = async ({
  summaryOptions,
  hasEditRole,
  user,
  getProtectedTenantCode,
  setGeneratingSummary,
  setSummaryProgress,
  setNotes,
  handleCloseSummaryModal,
  fetchNotes,
  t,
  toast
}) => {
  if (!hasEditRole) return;
  
  try {
    setGeneratingSummary(true);
    setSummaryProgress({
      step: 'gathering',
      current: 0,
      total: 3,
      message: 'Gathering notes...'
    });

    // Step 1: Gather notes
    const notes = await gatherNotesForSummary(summaryOptions, user, getProtectedTenantCode);

    if (!notes || notes.length === 0) {
      toast.error(t('Notes.noNotesFound') || 'No notes found matching the criteria');
      setGeneratingSummary(false);
      return;
    }

    console.log(`📝 Processing ${notes.length} notes for summary`);

    // Step 1.5: Fetch and summarize attachments if enabled
    let attachmentSummaries = [];
    if (summaryOptions.includeAttachments) {
      try {
        setSummaryProgress({
          step: 'fetching_attachments',
          current: 1,
          total: summaryOptions.includeAttachments ? 4 : 3,
          message: 'Fetching attachments...'
        });

        // Extract all note IDs from gathered notes
        const noteIds = notes.map(note => note.NoteID).filter(Boolean);
        
        if (noteIds.length > 0) {
          console.log(`📎 Fetching attachments for ${noteIds.length} notes`);

          // Fetch attachments for all notes
          const tenantCode = getProtectedTenantCode();
          const selectedRole = localStorage.getItem('selectedRole');
          const headers = {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {}),
            ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
          };

          // Fetch attachments for each note and combine
          const allAttachments = [];
          for (const noteId of noteIds) {
            const response = await fetch(`/api/attachments/SalesNote/${noteId}`, { headers });
            if (response.ok) {
              const data = await response.json();
              const attachments = data.attachments || [];
              
              // Filter to only include document types (PDF, Word, Excel)
              const documentAttachments = attachments.filter(att => {
                if (att.AttachmentType === 'URL') return false;
                const mimeType = att.MimeType || '';
                const fileName = att.OriginalFileName || att.FileName || '';
                return (
                  mimeType.includes('pdf') ||
                  mimeType.includes('word') ||
                  mimeType.includes('msword') ||
                  mimeType.includes('wordprocessingml') ||
                  mimeType.includes('excel') ||
                  mimeType.includes('spreadsheet') ||
                  mimeType.includes('ms-excel') ||
                  fileName.endsWith('.pdf') ||
                  fileName.endsWith('.doc') ||
                  fileName.endsWith('.docx') ||
                  fileName.endsWith('.xls') ||
                  fileName.endsWith('.xlsx')
                );
              });
              
              allAttachments.push(...documentAttachments);
            }
          }

          if (allAttachments.length > 0) {
            console.log(`📎 Found ${allAttachments.length} document attachments to process`);

            setSummaryProgress({
              step: 'summarizing_attachments',
              current: 2,
              total: 4,
              message: `Summarizing ${allAttachments.length} attachments...`
            });

            // Send attachment IDs to backend for summarization
            const attachmentIds = allAttachments.map(att => att.AttachmentID);
            const summaryResponse = await fetch('/api/ai/chat/summarize-attachments', {
              method: 'POST',
              headers,
              body: JSON.stringify({ attachmentIds })
            });

            if (summaryResponse.ok) {
              const summaryData = await summaryResponse.json();
              attachmentSummaries = summaryData.summaries || [];
              
              console.log(`✅ Summarized ${attachmentSummaries.length} attachments`);
              console.log(`⚠️ Failed to process ${summaryData.errors?.length || 0} attachments`);

              // Show warnings for failed attachments
              if (summaryData.errors && summaryData.errors.length > 0) {
                toast.error(`Warning: ${summaryData.errors.length} attachment(s) could not be processed`);
              }
            } else {
              console.error('❌ Failed to summarize attachments');
              toast.error('Failed to summarize attachments. Continuing without attachment content.');
            }
          } else {
            console.log('📎 No document attachments found in selected notes');
          }
        }
      } catch (attachmentError) {
        console.error('Error processing attachments:', attachmentError);
        toast.error('Error processing attachments. Continuing without attachment content.');
      }
    }

    setSummaryProgress({
      step: 'processing',
      current: summaryOptions.includeAttachments ? 3 : 1,
      total: summaryOptions.includeAttachments ? 4 : 3,
      message: `Processing ${notes.length} notes...`
    });

    // Step 2: Get the prompt style with language support
    const styleKey = summaryOptions.style || 'focus_points';
    const language = summaryOptions.language || 'th';
    const languageInstruction = language === 'th' 
      ? '\n\nIMPORTANT: Write the ENTIRE summary in Thai language (ภาษาไทย). All sections, headings, and content must be in Thai. Do NOT translate author names or usernames to Thai—keep them exactly as provided (e.g. keep English names in English).'
      : '\n\nIMPORTANT: Write the ENTIRE summary in English language. All sections, headings, and content must be in English. Keep author names and usernames exactly as provided.';

    const styleConfig = {
      focus_points: {
        prompt: "You are an expert analyst. Analyze the following sales notes and create a concise summary focusing on the KEY POINTS and HIGHLIGHTS.\n\nNotes to analyze:\n{notes}\n\nInstructions:\n- Identify the most important information\n- Extract key decisions, commitments, and action items\n- Highlight critical insights and opportunities\n- Use bullet points for clarity\n- Keep the summary concise and actionable\n- Focus on what matters most" + languageInstruction + "\n\nFormat:\n## Key Focus Points\n\n### Critical Highlights\n- [Main highlights]\n\n### Important Decisions\n- [Key decisions made]\n\n### Action Items\n- [What needs to be done]\n\n### Opportunities\n- [Potential opportunities identified]\n\nCreate the summary now:",
        tags: ['summary', 'focus-points', 'highlights']
      },
      manager_report: {
        prompt: "You are a senior sales analyst preparing a report for management. Analyze the following sales notes and create a comprehensive MANAGER REPORT.\n\nNotes to analyze:\n{notes}\n\nInstructions:\n- Provide an executive summary suitable for management\n- Include key metrics, progress, and outcomes\n- Highlight wins, challenges, and risks\n- Identify team performance insights\n- Present strategic recommendations\n- Use professional business language\n- Be objective and data-driven where possible" + languageInstruction + "\n\nFormat:\n## Executive Summary\n\n### Overview\n[High-level summary of the period]\n\n### Key Achievements\n- [Major wins and successes]\n\n### Challenges & Risks\n- [Issues that need attention]\n\n### Pipeline Status\n- [Deal progress and forecast]\n\n### Team Performance\n- [Insights on team activities]\n\n### Strategic Recommendations\n- [Actions for leadership to consider]\n\nCreate the report now:",
        tags: ['summary', 'manager-report', 'executive-summary']
      },
      sales_strategy: {
        prompt: "You are a strategic sales consultant. Analyze the following sales notes and create a STRATEGIC ANALYSIS with actionable recommendations.\n\nNotes to analyze:\n{notes}\n\nInstructions:\n- Identify patterns and trends in customer interactions\n- Analyze deal progression and sales cycles\n- Evaluate customer needs and pain points\n- Assess competitive positioning\n- Provide strategic recommendations for closing deals\n- Suggest next best actions\n- Focus on winning strategies" + languageInstruction + "\n\nFormat:\n## Sales Strategy Analysis\n\n### Market & Customer Insights\n[What we learned about customers and market]\n\n### Deal Analysis\n- Pipeline health: [assessment]\n- Win probability: [factors affecting success]\n- Sales cycle: [timeline and stages]\n\n### Competitive Position\n[Our standing vs competitors]\n\n### Customer Needs & Pain Points\n- [Key requirements identified]\n- [Problems we can solve]\n\n### Strategic Recommendations\n1. [Top priority action]\n2. [Second priority]\n3. [Additional strategies]\n\n### Next Best Actions\n- [Immediate steps to take]\n\nCreate the strategy now:",
        tags: ['summary', 'strategy', 'analysis']
      },
      short_note: {
        prompt: "You are a professional note-taker. Analyze the following sales notes and create a BRIEF, CONCISE SUMMARY.\n\nNotes to analyze:\n{notes}\n\nInstructions:\n- Keep it short and to the point\n- Include only the most essential information\n- Use clear, simple language\n- Focus on what, who, when, and next steps\n- Maximum 3-5 paragraphs" + languageInstruction + "\n\nFormat:\n## Quick Summary\n\n**Overview:**\n[One paragraph summary of what happened]\n\n**Key Points:**\n- [Point 1]\n- [Point 2]\n- [Point 3]\n\n**Next Steps:**\n- [What to do next]\n\nCreate the summary now:",
        tags: ['summary', 'brief', 'quick-note']
      }
    };

    const selectedStyle = styleConfig[styleKey] || styleConfig.focus_points;

    // Add additional instructions if provided
    let finalPrompt = selectedStyle.prompt;
    if (summaryOptions.additionalInstructions && summaryOptions.additionalInstructions.trim()) {
      const additionalInstructionsText = `\n\n### ADDITIONAL INSTRUCTIONS FROM USER:\n${summaryOptions.additionalInstructions.trim()}\n\nPlease incorporate these additional instructions into your summary.`;
      finalPrompt = selectedStyle.prompt + additionalInstructionsText;
      console.log('📝 Additional instructions added to prompt:', summaryOptions.additionalInstructions);
    }

    // Step 3: Handle chunking if needed
    let summaryText = '';
    
    if (notes.length <= 20) {
      // Direct summarization for small datasets
      setSummaryProgress({
        step: 'summarizing',
        current: summaryOptions.includeAttachments ? 4 : 2,
        total: summaryOptions.includeAttachments ? 4 : 3,
        message: 'Creating summary...'
      });
      
      summaryText = await callAIForSummary(finalPrompt, notes, getProtectedTenantCode, attachmentSummaries);
    } else {
      // Chunk processing for large datasets
      const chunks = chunkNotes(notes, 20);
      const chunkSummaries = [];

      setSummaryProgress({
        step: 'chunking',
        current: summaryOptions.includeAttachments ? 4 : 2,
        total: summaryOptions.includeAttachments ? 4 : 3,
        message: `Processing ${chunks.length} chunks...`
      });

      // Summarize each chunk
      for (let i = 0; i < chunks.length; i++) {
        setSummaryProgress({
          step: 'chunking',
          current: summaryOptions.includeAttachments ? 4 : 2,
          total: summaryOptions.includeAttachments ? 4 : 3,
          message: `Processing chunk ${i + 1} of ${chunks.length}...`
        });

        const chunkPrompt = `You are analyzing a large collection of notes in chunks. Summarize this chunk of notes:\n\n{notes}\n\nProvide a concise summary of the key points in this chunk. Focus on:\n- Main topics discussed\n- Important decisions or commitments\n- Action items\n- Customer interactions\n- Deal progress\n\nSummary:`;
        
        const chunkSummary = await callAIForSummary(chunkPrompt, chunks[i], getProtectedTenantCode);
        chunkSummaries.push(chunkSummary);
      }

      // Final consolidation
      setSummaryProgress({
        step: 'consolidating',
        current: summaryOptions.includeAttachments ? 4 : 2,
        total: summaryOptions.includeAttachments ? 4 : 3,
        message: 'Creating final summary...'
      });

      // Add attachment summaries to the consolidation
      let consolidationContent = `Here are summaries of note chunks:\n\n${chunkSummaries.map((s, i) => `Chunk ${i + 1}:\n${s}`).join('\n\n---\n\n')}`;
      
      if (attachmentSummaries && attachmentSummaries.length > 0) {
        consolidationContent += '\n\n## Attached Documents\n\n' + 
          attachmentSummaries.map((att, idx) => {
            return `[Attachment ${idx + 1}] ${att.fileName} (${att.fileType})\nSummary: ${att.summary}\n`;
          }).join('\n---\n\n');
      }

      const consolidationPrompt = finalPrompt.replace(
        '{notes}',
        `${consolidationContent}\n\nBased on these chunk summaries${attachmentSummaries.length > 0 ? ' and attached documents' : ''}, create the final ${styleKey.replace('_', ' ')} summary:`
      );

      summaryText = await callAIForSummary(consolidationPrompt, [], getProtectedTenantCode);
    }

    // Step 4: Modify summary text to add date period and owner info on first ## line
    if (summaryText && notes.length > 0) {
      // Use English/username for owner (do not use translated Thai when not provided)
      const ownerName = getSummaryOwnerDisplayName(user);
      summaryText = formatSummaryFirstLine(summaryText, notes, summaryOptions.scope, ownerName);
    }

    // Step 5: Create the summary as a new note
    setSummaryProgress({
      step: 'saving',
      current: 3,
      total: 3,
      message: 'Saving summary as new note...'
    });

    // Style name mapping for display
    const styleNameMap = {
      focus_points: 'Focus Points',
      manager_report: 'Manager Report',
      sales_strategy: 'Sales Strategy',
      short_note: 'Short Note'
    };
    const styleName = styleNameMap[styleKey] || styleKey;

    // Build filter criteria description and tags
    let criteriaDescription = '';
    let criteriaTags = ['summary']; // Always include "summary" tag
    const criteriaParts = [];
    
    // Note type filter
    if (summaryOptions.noteTypes && Array.isArray(summaryOptions.noteTypes) && summaryOptions.noteTypes.length > 0) {
      const typesStr = summaryOptions.noteTypes.join(', ');
      criteriaParts.push(`Types: ${typesStr}`);
      // Add note types as tags (lowercase)
      summaryOptions.noteTypes.forEach(type => {
        if (type) criteriaTags.push(type.toLowerCase());
      });
    }
    
    // Tag filter
    if (summaryOptions.filterBy === 'tag' && summaryOptions.filterValue) {
      criteriaParts.push(`Tag: "${summaryOptions.filterValue}"`);
      criteriaTags.push(summaryOptions.filterValue);
    }
    
    // Date filter
    if (summaryOptions.filterBy === 'date' && (summaryOptions.dateFrom || summaryOptions.dateTo)) {
      const fromDate = summaryOptions.dateFrom || 'beginning';
      const toDate = summaryOptions.dateTo || 'now';
      criteriaParts.push(`Date Range: ${fromDate} to ${toDate}`);
    } else if ((summaryOptions.dateFrom || summaryOptions.dateTo) && summaryOptions.filterBy !== 'date') {
      // Date filter can also be applied alongside other filters (e.g., with tag filter)
      const fromDate = summaryOptions.dateFrom || 'beginning';
      const toDate = summaryOptions.dateTo || 'now';
      criteriaParts.push(`Date Range: ${fromDate} to ${toDate}`);
    }
    
    // Entity filter
    if (summaryOptions.filterBy === 'entity' && summaryOptions.entityType && summaryOptions.entityName) {
      criteriaParts.push(`Related to ${summaryOptions.entityType}: ${summaryOptions.entityName}`);
      criteriaTags.push(summaryOptions.entityType.toLowerCase());
    }
    
    // Build final description
    if (criteriaParts.length > 0) {
      criteriaDescription = criteriaParts.join(' | ');
    } else {
      criteriaDescription = summaryOptions.scope === 'my' ? 'All My Notes' : 'All Notes';
    }

    // Remove duplicates
    const uniqueTags = [...new Set(criteriaTags)];

    // Add metadata to summary with criteria and style information (explicit timezone)
    const generatedTime = new Date().toLocaleString('en-US', {
      timeZone: SUMMARY_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    const attachmentInfo = attachmentSummaries.length > 0 
      ? `\n- **Attachments Processed:** ${attachmentSummaries.length}`
      : '';
    const summaryWithMeta = `${summaryText}\n\n---\n\n**Summary Details**\n**Criteria:** ${criteriaDescription} | **Generated:** ${generatedTime} (${SUMMARY_TIMEZONE_LABEL})\n- **Style:** ${styleName}\n- **Notes Analyzed:** ${notes.length}${attachmentInfo}\n\n*This summary was automatically generated using AI.*`;

    console.log('📝 Creating summary note...');
    console.log('Summary length:', summaryText.length);
    console.log('Summary with meta length:', summaryWithMeta.length);
    console.log('Summary style:', styleName);
    console.log('Tags:', uniqueTags.join(', '));

    // Create the note
    const tenantCode = getProtectedTenantCode();
    const selectedRole = localStorage.getItem('selectedRole');
    const response = await fetch('/api/sales-notes', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {}),
        ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
      },
      body: JSON.stringify({ 
        noteText: summaryWithMeta, 
        tags: uniqueTags.join(', '),
        type: 'Summary'
      })
    });

    if (response.ok) {
      const newNote = await response.json();
      console.log('✅ Summary note created:', newNote);
      setNotes(prev => [newNote, ...prev]);
      
      toast.success(t('Notes.summaryCreated') || 'Summary created successfully!');
      handleCloseSummaryModal();
      
      // Refresh notes
      fetchNotes();
    } else {
      const errorData = await response.json();
      console.error('❌ Failed to create note:', errorData);
      throw new Error(errorData.message || 'Failed to save summary note');
    }

  } catch (error) {
    console.error('Error generating summary:', error);
    toast.error(t('Notes.summaryFailed') || `Failed to generate summary: ${error.message}`);
  } finally {
    setGeneratingSummary(false);
    setSummaryProgress({
      step: '',
      current: 0,
      total: 0,
      message: ''
    });
  }
};
